import React, { useState } from 'react'
import { confirmOrder } from '../../services/api/consumer'
import { ConfirmConsumerV2OrderRequest, ConsumerOrderDetails } from '../../model'
import { useForm } from 'react-hook-form'
import Button from '../../components/Button/Button'
import IconWithBackground from '../../components/IconWithBackground/IconWithBackground'
import PackageItem from '../../components/PackageItem/PackageItem'
import ProductItem from '../../components/ProductItem/ProductItem'
import Invoice3rdPartyConfirmForm, {
    Invoice3rdPartyConfirmFormValues
} from '../../components/NewOrder/Invoice3rdPartyConfirmForm'
import { formatCurrencyValue } from '../../helpers/utils'
import DynamicFaIcon from '../../components/DynamicFaIcon/DynamicFaIcon'

interface ConfirmOrderConfirmProps {
    initialOrderData: ConsumerOrderDetails
    onConfirmed: () => void
}

type OrderLineWithMetadata = {
    id?: string | number
    _isPack?: boolean
    [key: string]: unknown
}

const ConfirmOrderConfirm: React.FunctionComponent<ConfirmOrderConfirmProps> = ({
    initialOrderData,
    onConfirmed
}: ConfirmOrderConfirmProps) => {
    const { register, formState, handleSubmit } = useForm<Invoice3rdPartyConfirmFormValues>({
        defaultValues: {
            invoiceName: initialOrderData.invoiceDetails?.invoiceName ?? '',
            invoiceEmail: initialOrderData.invoiceDetails?.invoiceEmail ?? '',
            invoiceAddress: initialOrderData.invoiceDetails?.invoiceAddress ?? '',
            invoiceZipcode: initialOrderData.invoiceDetails?.invoiceZipcode ?? '',
            invoiceCity: initialOrderData.invoiceDetails?.invoiceCity ?? ''
        },
        mode: 'onChange',
        reValidateMode: 'onChange'
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const orderLines = (initialOrderData.order?.lines ?? []) as OrderLineWithMetadata[]
    const canConfirmOrder = initialOrderData.order?.state === 'requested'

    const onConfirmOrder = async (data: Invoice3rdPartyConfirmFormValues) => {
        const request: ConfirmConsumerV2OrderRequest = {
            invoiceName: data.invoiceName.trim(),
            invoiceAddress: data.invoiceAddress.trim(),
            invoiceZipcode: data.invoiceZipcode.trim(),
            invoiceCity: data.invoiceCity.trim()
        }

        try {
            setError(null)
            setLoading(true)
            await confirmOrder(request)
            onConfirmed()
        } catch (error) {
            console.error('Error confirming order:', error)
            setError('Er is een fout opgetreden bij het bevestigen van de opdracht. Probeer het later opnieuw.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className='flex flex-col space-y-4'>
            <div className='flex flex-col space-y-2'>
                <h1 className='text-2xl font-semibold text-center text-[--tb-mainHeading]'>
                    {initialOrderData.order?.state === 'requested'
                        ? 'Controleer je bestelling'
                        : 'Bestelling bevestigd'}
                </h1>
            </div>
            <div className='flex flex-col space-y-2'>
                <h3 className='mb-1'>Adres</h3>
                <div className='flex items-start my-2 pb-3'>
                    <IconWithBackground size='small' iconName='house' />
                    <div className='flex flex-col text-xs ml-3'>
                        <span>
                            {initialOrderData.order?.address?.street} {initialOrderData.order?.address?.houseNumber}
                            {initialOrderData.order?.address?.extension ?? ''}
                        </span>
                        <span>
                            {initialOrderData.order?.address?.city}, {initialOrderData.order?.address?.zipcode}
                        </span>
                        {initialOrderData.order?.address?.surface && (
                            <span>
                                Woonoppervlakte: {initialOrderData.order?.address?.surface} m<sup>2</sup>
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className='mb-4 -mx-4'>
                <h3 className='mb-1 px-4'>Jouw producten</h3>
                {orderLines.length > 0 ? (
                    <>
                        {orderLines.map((product, index) => {
                            return (
                                <div className='flex items-center gap-x-2' key={`${index}-${product.id}`}>
                                    {product._isPack ? (
                                        <PackageItem layout='row' a={product as any} />
                                    ) : (
                                        <ProductItem
                                            showHighlight={false}
                                            showOptions
                                            product={product}
                                            productInBasket={product as any}
                                            className='last:border-b-0'
                                        />
                                    )}
                                    <span></span>
                                </div>
                            )
                        })}
                        {/* {extraOptions &&
                            extraOptions.length > 0 &&
                            extraOptions.map((a: any, index: number) => (
                                <div key={index} className='flex items-center gap-x-2'>
                                    <div className='flex flex-grow px-4 justify-between items-center py-3 '>
                                        <div className='flex items-center'>
                                            <span className='italic text-sm ml-16'>{a.name}:</span>
                                        </div>
                                        <div className='space-x-4 flex items-center'>
                                            <span className='px-4 text-sm'>{formatCurrencyValue(a.price)}</span>
                                        </div>
                                    </div>
                                    <div className='w-20'></div>
                                </div>
                            ))} */}

                        <div className='pl-6 pr-4 py-6 text-right text-sm border-btm'>
                            Totaal:{' '}
                            <span className='font-semibold'>
                                {formatCurrencyValue(initialOrderData.order?.totalAmount || 0)}
                            </span>
                            <span className='text-xs font-normal text-gray-500 ml-1'>incl. BTW</span>
                            <p className='text-xs italic text-gray-500 mt-1'>prijs onder voorbehoud</p>
                        </div>
                    </>
                ) : (
                    <p className='text-sm text-gray-500 px-4'>Geen producten gevonden</p>
                )}
            </div>
            {canConfirmOrder && (
                <>
                    <div className='flex flex-col space-y-2'>
                        <Invoice3rdPartyConfirmForm register={register} formState={formState} />
                    </div>
                    <Button
                        type='submit'
                        onClick={handleSubmit(onConfirmOrder)}
                        disabled={!formState.isValid || loading}
                        className='self-end justify-self-end'
                    >
                        {loading ? (
                            <>
                                <DynamicFaIcon iconName='spinner' className='mr-2' spin />
                                Bevestigen..
                            </>
                        ) : (
                            <span>
                                <DynamicFaIcon iconName='check' className='mr-2' />
                                Opdracht bevestigen
                            </span>
                        )}
                    </Button>
                    {error && <p className='text-red-700 mt-3 text-sm'>{error}</p>}
                </>
            )}
        </div>
    )
}
export default ConfirmOrderConfirm
